import * as React from 'react';
import { Button, Stack, Tab, Tabs } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AbsencePeriodUser,
  Attendance,
  BookingExtendedView,
  BookingExtendedViewRow,
  Event,
  EventAudit,
  EventFeedback,
  Institution,
  InstitutionGroupMember,
  InstitutionGroupMemberRow,
  InstitutionMember,
  InstitutionMemberRow,
  OutboundEmail,
  PushNotificationLog,
  ReportInstitutionUserView,
  TaskUserView,
  UserBookingComputedStatusOptions,
  UserView,
} from 'cb-model';
import {
  addControlColumn,
  CalendarView,
  ColourKey,
  CalendarViewEvent,
  DataPills,
  Display,
  IsAllowed,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  selectUser,
  useCalendarRange,
  useCacheQuery,
  useCacheRecord,
  useCloneEntity,
  UneditableReference,
} from 'gossamer-front-end';
import { Entity, getFilter } from 'gossamer-unified';
import { AddMemberToGroupButton } from './AddMemberToGroupButton';
import { AdminCreateAttendanceForMemberButton } from '../attendance/AdminCreateAttendanceForMemberButton';
import { BookUserToEventButton } from './BookUserToEventButton';
import { RemoveMemberFromGroupButton } from '../institution-group/RemoveMemberFromGroupButton';
import { MemberRevokeMembershipButton } from '../institution-member/MemberRevokeMembershipButton';
import { UnverifiedMemberAcceptButton } from '../institution-member/UnverifiedMemberAcceptButton';
import { VerifiedMemberMakeAdminButton } from './VerifiedMemberMakeAdminButton';
import { VerifiedMemberMakeNonAdminButton } from './VerifiedMemberMakeNonAdminButton';
import { SummaryChartTable, useDateRange } from '../reporting';
import { CustomInfo } from 'cb-unified';
import { Task, WORKFLOW_GROUP_TYPES } from 'gossamer-model';
import { UnsuccessfulLoginAttemptsResetButton } from './UnsuccessfulLoginAttemptsResetButton';
import { DeactivateUserButton } from './DeactivateUserButton';
import { SkipAllEmailSendingButton } from './SkipAllEmailSendingButton';
import { ChangeEmailAddressButton } from './ChangeEmailAddressButton';
import { BookingModal } from '../institution-group/BookingModal';

export const UserDisplay = (): React.JSX.Element => {
  const params = useParams();
  const userId = params.key ?? '';
  const record = useCacheRecord(UserView, userId);
  const [searchParams, setSearchParams] = useSearchParams();
  // prevent infinite re-render
  const now = React.useRef(new Date().toISOString());
  const user = useSelector(selectUser);
  const reportingDateRange = useDateRange();
  const staffInstitutionId = searchParams.get('staffInstitutionId');
  const adminInstitutionId = (user?.customInfo as CustomInfo)?.adminInstitutionId ?? undefined;
  const membershipFilters = [getFilter('user_id', 'eq', userId)];
  if (user?.roleId === 'admin') {
    membershipFilters.push(getFilter('institution_id', 'eq', adminInstitutionId));
  }
  const membershipQuery = useCacheQuery(InstitutionMember, { where: { and: membershipFilters } }, !user);
  React.useEffect(() => {
    const latestMembership = (membershipQuery.data || [])
      .slice()
      .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))[0]; // default to the latest membership
    if (latestMembership && !staffInstitutionId) {
      const changedSearchParams = new URLSearchParams(searchParams);
      changedSearchParams.set('staffInstitutionId', String(latestMembership?.institution_id));
      setSearchParams(changedSearchParams, { replace: true });
    }
  }, [membershipQuery.data]);
  const isAdminForThisUser = user?.roleId === 'staff' || (user?.roleId === 'admin' && !!membershipQuery.data?.length);
  const reportingInstitutionId = user?.roleId === 'admin' ? String(adminInstitutionId) : staffInstitutionId;
  const reportingQuery = useCacheQuery(
    ReportInstitutionUserView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', userId),
          getFilter('week_commencing', 'gt', reportingDateRange.start.toISOString()),
          getFilter('week_commencing', 'lt', reportingDateRange.end.toISOString()),
          getFilter('institution_id', 'eq', reportingInstitutionId),
        ],
      },
      order: [['week_commencing', 'ASC']],
    },
    !reportingInstitutionId,
  );

  const LocalReportInstitutionUserView = useCloneEntity(ReportInstitutionUserView, (local) => {
    local.fields.user_id.listColumn = false;
    local.fields.institution_id.listColumn = false;
  });

  const LocalInstitutionGroupMember = useCloneEntity(InstitutionGroupMember, (local) => {
    addControlColumn(
      local,
      'remove',
      999,
      (row: InstitutionGroupMemberRow) => String(row.id),
      RemoveMemberFromGroupButton,
    );
  });

  return (
    <>
      <NavIconContainer entityId={UserView.id} />
      <PageTitle verb="Display" entity={UserView} keyValue={userId} subtitle="Details relating to this User" />

      <Tabs
        activeKey={searchParams.get('tab') || (isAdminForThisUser ? 'overview' : 'main')}
        variant="underline"
        mountOnEnter={true}
        unmountOnExit={true}
        onSelect={(tab) => {
          if (!tab) {
            return;
          }
          const newParams = new URLSearchParams(searchParams);
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="overview" title="Overview">
          {isAdminForThisUser && (
            <Section>
              {user?.roleId === 'staff' && (
                <UneditableReference refEntity={Institution} value={reportingInstitutionId} />
              )}
              <SummaryChartTable
                data={reportingQuery.data || []}
                entity={LocalReportInstitutionUserView}
                period={reportingDateRange}
                hideEvents={true}
              />
            </Section>
          )}

          <Section title="Basic Details">
            <QuerySingle entity={UserView} keyValue={userId} render={Display} fieldGroup="display" />
            {isAdminForThisUser && (
              <Stack direction="horizontal" gap={3}>
                <Link to={`/admin/user/update-person/${record.data?.person_id}`}>
                  <Button size="sm" variant="primary">
                    Update
                  </Button>
                </Link>
                {membershipQuery.data?.[0]?.id &&
                  membershipQuery.data[0].is_verified &&
                  !membershipQuery.data[0].is_admin && (
                    <VerifiedMemberMakeAdminButton value={String(membershipQuery.data[0].id)} />
                  )}
                {membershipQuery.data?.[0]?.id && membershipQuery.data[0].is_admin && (
                  <VerifiedMemberMakeNonAdminButton value={String(membershipQuery.data[0].id)} />
                )}
                {membershipQuery.data?.[0]?.id && !membershipQuery.data[0].is_verified && (
                  <UnverifiedMemberAcceptButton value={String(membershipQuery.data[0].id)} />
                )}
                {membershipQuery.data?.[0]?.id &&
                  membershipQuery.data[0].is_verified &&
                  !membershipQuery.data[0].is_admin && (
                    <MemberRevokeMembershipButton value={String(membershipQuery.data[0].id)} />
                  )}
                <UnsuccessfulLoginAttemptsResetButton user_id={userId} />
                {user?.roleId === 'staff' && record.data?.email && (
                  <Link to={`/auth/chameleon-login?email=${encodeURIComponent(record.data.email)}`}>
                    <Button size="sm" variant="warning">
                      Log in as this user
                    </Button>
                  </Link>
                )}
              </Stack>
            )}
          </Section>

          {user.roleId === 'staff' && (
            <Section title="Institution Membership / Deactivation" subtitle="Staff user access only">
              <QueryMulti
                alterEntity={(cloneEntity: Entity<InstitutionMemberRow>) => {
                  cloneEntity.listRowLink = null;
                  cloneEntity.fields.user_id.listColumn = false;
                  addControlColumn(
                    cloneEntity,
                    'change_staff_inst',
                    999,
                    (row) => String(row.institution_id),
                    (props) => (
                      <Link to={`?staffInstitutionId=${props.value}`}>
                        <Button size="sm" variant="secondary">
                          Switch
                        </Button>
                      </Link>
                    ),
                  );
                }}
                entity={InstitutionMember}
                render={List}
                queryOptions={{
                  where: {
                    and: [getFilter('user_id', 'eq', userId)],
                  },
                }}
              />
              <Stack direction="horizontal" gap={3} className="mb-3">
                <Link to={`/admin/institution-member/update-grid/${userId}`}>
                  <Button size="sm" variant="secondary">
                    Update Institution Membership
                  </Button>
                </Link>
                <DeactivateUserButton userId={userId} />
                <SkipAllEmailSendingButton userId={userId} />
              </Stack>
              <ChangeEmailAddressButton userId={userId} />
            </Section>
          )}

          {isAdminForThisUser && !!record.data && !!membershipQuery.data?.[0]?.id && (
            <Section title="Groups" subtitle="Groups this Member is part of">
              <QueryMulti
                entity={LocalInstitutionGroupMember}
                alterEntity={(localEntity: typeof LocalInstitutionGroupMember) => {
                  localEntity.fields.institution_member_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('institution_member_id', 'eq', membershipQuery.data[0].id)],
                  },
                }}
                render={List}
              />
              {!!adminInstitutionId && (
                <>
                  <div className="mb-3" />
                  <AddMemberToGroupButton emailAddress={record.data.email} institutionId={adminInstitutionId} />
                </>
              )}
            </Section>
          )}
        </Tab>

        <Tab eventKey="bookings" title="Bookings">
          <Section title="Bookings Calendar">
            <UserBookingsCalendar userId={userId} />
            <div className="mb-3" />
            {membershipQuery.data?.[0]?.is_verified &&
              (user?.roleId === 'staff' ||
                (user?.roleId === 'admin' &&
                  (user.customInfo as CustomInfo)?.adminInstitutionType === 'enterprise')) && (
                <BookUserToEventButton userId={userId} />
              )}
          </Section>
        </Tab>

        <Tab eventKey="attendance" title="Attendance">
          <Section title="Attendance Summary by Week">
            <QueryMulti
              entity={ReportInstitutionUserView}
              alterEntity={(localEntity: typeof ReportInstitutionUserView) => {
                localEntity.fields.user_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('user_id', 'eq', userId)],
                },
              }}
              render={List}
            />
          </Section>
          <Section title="Attendance Records">
            <QueryMulti
              entity={Attendance}
              alterEntity={(localEntity: typeof Attendance) => {
                localEntity.fields.user_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('user_id', 'eq', userId)],
                },
              }}
              render={List}
            />
            <div className="mb-3" />
            <IsAllowed entity={Attendance} action="create">
              <AdminCreateAttendanceForMemberButton userId={userId} />
            </IsAllowed>
          </Section>
        </Tab>

        <Tab eventKey="absence" title="Absence">
          <Section title="Absence Period Status" subtitle="RAG status for defined absence tracking periods">
            <QueryMulti
              entity={AbsencePeriodUser}
              alterEntity={(localEntity: typeof AbsencePeriodUser) => {
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.institution_member_id.listColumn = false;
              }}
              queryOptions={{
                where: {
                  and: [getFilter('user_id', 'eq', userId)],
                },
                order: [['absence_period_id', 'DESC']],
              }}
              render={List}
            />
          </Section>
          <Section title="Absence Records">
            <QueryMulti
              entity={Attendance}
              alterEntity={(localEntity: typeof Attendance) => {
                localEntity.fields.recording_method.listColumn = false;
                localEntity.fields.user_id.listColumn = false;
                localEntity.fields.created_at.listColumn = false;
                localEntity.fields.attendance_result.listColumn = true;
                localEntity.fields.absence_reason.listColumn = true;
                localEntity.fields.event_start_at.listColumn = true;
              }}
              queryOptions={{
                where: {
                  and: [
                    getFilter('user_id', 'eq', userId),
                    getFilter('attendance_result', 'ne', 'attended'),
                    getFilter('event_start_at', 'lt', now.current),
                  ],
                },
                order: [['event_start_at', 'DESC']],
              }}
              render={List}
            />
          </Section>
        </Tab>

        {isAdminForThisUser && (
          <Tab eventKey="feedback" title="Feedback">
            <Section title="Feedback Provided">
              <QueryMulti
                entity={EventFeedback}
                alterEntity={(localEntity: typeof EventFeedback) => {
                  localEntity.fields.submitter_user_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('submitter_user_id', 'eq', userId)],
                  },
                }}
                render={List}
              />
            </Section>
          </Tab>
        )}

        {isAdminForThisUser && (
          <Tab eventKey="hosted" title="Hosted Events">
            <Section title="Hosted Events">
              <QueryMulti
                entity={Event}
                alterEntity={(localEntity: typeof Event) => {
                  localEntity.fields.main_host_user_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('main_host_user_id', 'eq', userId)],
                  },
                }}
                render={List}
              />
            </Section>
            <Section title="Event Updates" subtitle="Audited changes of event details made by this user">
              <QueryMulti
                entity={EventAudit}
                alterEntity={(localEntity: typeof EventAudit) => {
                  localEntity.fields.performed_by.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('performed_by', 'eq', userId)],
                  },
                }}
                render={List}
              />
            </Section>
          </Tab>
        )}

        {user?.roleId === 'staff' && (
          <Tab eventKey="notifications" title="Notifications">
            <Section title="Outbound Emails">
              <QueryMulti
                entity={OutboundEmail}
                alterEntity={(localEntity: typeof OutboundEmail) => {
                  localEntity.fields.to_email_address.listColumn = false;
                  localEntity.fields.to_user_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('to_user_id', 'eq', userId)],
                  },
                }}
                render={List}
              />
            </Section>
            <Section title="Push Notifications" subtitle="History of push notifications sent to this user">
              <QueryMulti
                entity={PushNotificationLog}
                alterEntity={(localEntity: typeof PushNotificationLog) => {
                  localEntity.fields.user_id.listColumn = false;
                  localEntity.fields.subscription_endpoint.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('user_id', 'eq', userId)],
                  },
                  order: [['created_at', 'DESC']],
                }}
                render={List}
              />
            </Section>
          </Tab>
        )}

        {isAdminForThisUser && (
          <Tab eventKey="tasks" title="Task Progress">
            <Section title="Summary">
              <QueryMulti
                entity={TaskUserView}
                render={List}
                alterEntity={(localEntity: typeof TaskUserView) => {
                  Object.values(localEntity.fields).forEach((field) => (field.listColumn = true));
                  localEntity.fields.user_id.listColumn = false;
                }}
                queryOptions={{
                  where: {
                    and: [getFilter('user_id', 'eq', params.key)],
                  },
                }}
              />
            </Section>
            <Section title="Completed Tasks" subtitle="Tasks completed by this user">
              <QueryMulti
                entity={Task}
                alterEntity={(localEntity: typeof Task) => {
                  Object.values(localEntity.fields).forEach((field) => (field.listColumn = false));
                  localEntity.fields.definition_id.listColumn = true;
                  localEntity.fields.completed_at.listColumn = true;
                }}
                queryOptions={{
                  where: {
                    and: [
                      getFilter('assigned_to', 'eq', params.key),
                      getFilter('status', 'eq', 'COMPLETED'),
                      getFilter('type', 'ni', WORKFLOW_GROUP_TYPES),
                    ],
                  },
                }}
                render={List}
              />
            </Section>
            <Section title="Active Tasks" subtitle="Tasks currently assigned to this user">
              <QueryMulti
                entity={Task}
                alterEntity={(localEntity: typeof Task) => {
                  Object.values(localEntity.fields).forEach((field) => (field.listColumn = false));
                  localEntity.fields.label.listColumn = true;
                }}
                queryOptions={{
                  where: {
                    and: [
                      getFilter('assigned_to', 'eq', params.key),
                      getFilter('status', 'eq', 'ACTIVE'),
                      getFilter('type', 'ni', WORKFLOW_GROUP_TYPES),
                    ],
                  },
                }}
                render={(props) => (
                  <DataPills field={Task.fields.label} fieldId="label" entity={props.entity} rows={props.rows} />
                )}
              />
            </Section>
            <Section title="Pending Tasks" subtitle="Tasks not yet available for this user">
              <QueryMulti
                entity={Task}
                alterEntity={(localEntity: typeof Task) => {
                  Object.values(localEntity.fields).forEach((field) => (field.listColumn = false));
                  localEntity.fields.label.listColumn = true;
                }}
                queryOptions={{
                  where: {
                    and: [
                      getFilter('assigned_to', 'eq', params.key),
                      getFilter('status', 'eq', 'PENDING'),
                      getFilter('type', 'ni', WORKFLOW_GROUP_TYPES),
                    ],
                  },
                }}
                render={(props) => (
                  <DataPills field={Task.fields.label} fieldId="label" entity={props.entity} rows={props.rows} />
                )}
              />
            </Section>
          </Tab>
        )}
      </Tabs>
    </>
  );
};

interface UserBookingCalendarEvent extends CalendarViewEvent {
  bgColor: string;
  textColor: string;
  booking: BookingExtendedViewRow;
}

const UserBookingsCalendar = (props: { userId: string }): React.JSX.Element => {
  const { range } = useCalendarRange();
  const navigate = useNavigate();
  const [selectedBooking, setSelectedBooking] = React.useState<UserBookingCalendarEvent | null>(null);
  const [stableBookings, setStableBookings] = React.useState<BookingExtendedViewRow[]>([]);
  const LocalBookingExtendedView = useCloneEntity(BookingExtendedView, (local) => {
    Object.values(local.fields).forEach((field) => {
      field.visible = false;
    });
    local.fields.type.visible = true;
    local.fields.start_at.visible = true;
    local.fields.end_at.visible = true;
    local.fields.location_type.visible = true;
    local.fields.location.visible = true;
    local.fields.visibility_limit.visible = true;
    local.fields.visibility_limit.formColumnWidths = { md: 12 };
    local.fields.flow_option.visible = true;
    local.fields.flow_option.formColumnWidths = { md: 12 };
    local.fields.ticket_title.visible = true;
    local.fields.booked_quantity.visible = true;
    local.fields.computed_status.visible = true;
  });
  const bookingsQuery = useCacheQuery(BookingExtendedView, {
    where: {
      and: [
        getFilter('user_id', 'eq', props.userId),
        getFilter('booking_status', 'eq', 'active'),
        getFilter('end_at', 'gt', range.start.toISOString()),
        getFilter('start_at', 'lt', range.end.toISOString()),
      ],
    },
    order: [['start_at', 'ASC']],
    limit: 1000,
  });
  React.useEffect(() => {
    if (bookingsQuery.data !== undefined) {
      setStableBookings(bookingsQuery.data);
    }
  }, [bookingsQuery.data]);
  const calendarBookings = bookingsQuery.data ?? stableBookings;

  const events = React.useMemo<UserBookingCalendarEvent[]>(
    () =>
      calendarBookings.map((booking) => {
        const option = UserBookingComputedStatusOptions.find(
          (option) => option.id === booking.computed_status.toLowerCase(),
        );
        return {
          id: String(booking.ticket_booking_id),
          title: booking.title || booking.ticket_title || `Event ${booking.event_id}`,
          start: new Date(booking.start_at),
          end: new Date(booking.end_at),
          bgColor: option?.bgColor || '#ACBDE9',
          textColor: option?.textColor || '#000000',
          booking,
        };
      }),
    [calendarBookings],
  );

  return (
    <>
      <CalendarView<UserBookingCalendarEvent>
        events={events}
        handleEventSelect={(event) => setSelectedBooking(event)}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.bgColor,
            borderColor: event.bgColor,
            color: event.textColor,
          },
        })}
      />
      <ColourKey items={UserBookingComputedStatusOptions} />
      {!!selectedBooking && (
        <BookingModal
          event_id={selectedBooking.booking.event_id}
          event_name={selectedBooking.booking.title || `Event ${selectedBooking.booking.event_id}`}
          visibility_limit={selectedBooking.booking.visibility_limit}
          flow_option={selectedBooking.booking.flow_option}
          user_id={selectedBooking.booking.user_id}
          start_at={selectedBooking.booking.start_at}
          end_at={selectedBooking.booking.end_at}
          booking_status={selectedBooking.booking.booking_status}
          ticket_booking_id={selectedBooking.booking.ticket_booking_id}
          ticket_title={selectedBooking.booking.ticket_title}
          attendance_id={selectedBooking.booking.attendance_id}
          attendance_result={selectedBooking.booking.attendance_result}
          event_feedback_id={selectedBooking.booking.event_feedback_id}
          context="user"
          show={true}
          setShow={(val) => {
            if (!val) setSelectedBooking(null);
          }}
          {...selectedBooking.booking}
        />
      )}
    </>
  );
};
